import React from 'react';
import benchmark from '../data/benchmarkSummary.json';

/**
 * A picture for each thing the product claims to give you.
 *
 * "What you get" was eight paragraphs in a row. Everything in it is true and
 * measured, and none of it meant anything to somebody who has not used the
 * product — a reader has to take "deterministic enforcement, with the reason"
 * entirely on faith. Each item now shows the thing rather than describing it:
 * a real screenshot where the answer is a screen, a small drawing where it is
 * an idea, and the actual measurement where it is a number.
 *
 * These are deliberately small and quiet. They sit above the words, not in
 * place of them, and none of them carries information the sentence does not.
 */

const FRAME = 'overflow-hidden rounded-xl border border-slate-900/10 bg-slate-50 dark:border-white/10 dark:bg-white/5';
/** One height for all of them, so the headings below line up across a row. */
const H = 132;

/** A screenshot of the real component, cropped to its top. */
export const ShotVisual: React.FC<{ src: string; alt: string; dark: boolean; shift?: number; height?: number }> = ({ src, alt, dark, shift = 0, height = H }) => (
  <div className={`${FRAME} relative`} style={{ height }}>
    <img
      src={`/media/${dark ? 'dark' : 'light'}/${src}.webp`}
      alt={alt}
      loading="lazy"
      decoding="async"
      className="block"
      // Full width, cropped only from the bottom. `object-fit: cover` on a box
      // this shape cuts the sides off instead, which is what truncated every
      // blocked action to `disable_audit_log…`.
      style={{ width: '100%', marginTop: shift ? `${shift}%` : undefined }}
    />
    {/* The crop lands mid-row; soften it so it reads as "more of this". */}
    <div
      className="pointer-events-none absolute inset-x-0 bottom-0 h-8"
      style={{
        background: dark
          ? 'linear-gradient(to top, rgba(9,12,22,0.95), rgba(9,12,22,0))'
          : 'linear-gradient(to top, rgba(248,250,252,0.95), rgba(248,250,252,0))'
      }}
    />
  </div>
);

/**
 * What the approval gate does, in one picture: without a signature every action
 * is held, with one the profile's own rules decide.
 */
export const GateVisual: React.FC = () => (
  <div className={`${FRAME} flex items-stretch gap-px p-0`} style={{ height: H }}>
    <div className="flex flex-1 flex-col justify-center gap-1 px-3 py-2">
      <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">Not yet approved</p>
      <div className="flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-300">every action held</span>
      </div>
      <p className="text-[9px] leading-3 text-slate-500 dark:text-slate-400">even read_invoice</p>
    </div>
    <div className="w-px bg-slate-200 dark:bg-white/10" />
    <div className="flex flex-1 flex-col justify-center gap-1 px-3 py-2">
      <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500">Signed by a person</p>
      <div className="flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">the profile decides</span>
      </div>
      <p className="text-[9px] leading-3 text-slate-500 dark:text-slate-400">allow · hold · block</p>
    </div>
  </div>
);

/**
 * The benchmark, drawn.
 *
 * Two rates over three approaches. Both are percentages of the same corpus, so
 * they share one axis — the point is precisely that the two baselines are
 * trivially perfect on one measure by being useless on the other, which is only
 * visible when both are drawn against the same scale.
 *
 * Two series, so identity is carried by a direct label on every bar as well as
 * by colour, and the figures are written out beside them.
 */
export const BenchmarkVisual: React.FC<{ dark: boolean }> = ({ dark }) => {
  const engine = benchmark.engine;
  const rows = [
    { name: 'AgenticScale', caught: engine.recall, flagged: engine.false_positive_rate, lead: true, note: '' },
    { name: 'Always allow', caught: benchmark.baselines.always_allow.recall, flagged: benchmark.baselines.always_allow.false_positive_rate, lead: false, note: 'catches nothing' },
    { name: 'Always review', caught: benchmark.baselines.always_review.recall, flagged: benchmark.baselines.always_review.false_positive_rate, lead: false, note: 'stops everything' }
  ];
  // Validated with the palette checker against both surfaces.
  const caughtHue = dark ? '#0e8ee9' : '#0270c7';
  const flaggedHue = dark ? '#d97706' : '#b45309';

  return (
    <figure className={`${FRAME} m-0 flex flex-col justify-center gap-2 px-3 py-2`} style={{ height: H }}>
      <div className="flex items-center gap-3">
        {[['Attacks caught', caughtHue], ['Safe actions stopped', flaggedHue]].map(([label, hue]) => (
          <span key={label} className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-[2px]" style={{ background: hue as string }} />
            <span className="text-[9px] font-medium text-slate-500 dark:text-slate-400">{label}</span>
          </span>
        ))}
      </div>
      {rows.map((row) => (
        <div key={row.name} className="flex items-center gap-2">
          <span className={`w-[74px] shrink-0 text-[9px] leading-3 ${row.lead ? 'font-bold text-slate-700 dark:text-slate-200' : 'text-slate-500 dark:text-slate-400'}`}>
            {row.name}
          </span>
          <div className="flex-1 space-y-[3px]">
            {[{ value: row.caught, hue: caughtHue }, { value: row.flagged, hue: flaggedHue }].map((bar, i) => (
              <div key={i} className="h-[6px] overflow-hidden rounded-[3px] bg-slate-200/70 dark:bg-white/10">
                <div className="h-full rounded-[3px]" style={{ width: `${bar.value * 100}%`, background: bar.hue }} />
              </div>
            ))}
          </div>
          {/* A zero-length bar and a missing bar look identical, so every value
              is written out as well as drawn — and the two that are zero say in
              words what that zero means. */}
          <span className="w-[86px] shrink-0 text-right font-mono text-[8.5px] leading-[9px] tabular-nums text-slate-500 dark:text-slate-400">
            {(row.caught * 100).toFixed(0)}% · {(row.flagged * 100).toFixed(0)}%
            {row.note && <span className="block font-sans text-[8px] not-italic text-slate-400 dark:text-slate-500">{row.note}</span>}
          </span>
        </div>
      ))}
    </figure>
  );
};

/** The chain that makes the format an open specification rather than a config file. */
export const SpecVisual: React.FC = () => (
  <div className={`${FRAME} flex flex-col justify-center gap-2 px-3 py-2`} style={{ height: H }}>
    {[
      ['Written spec', 'RFC 2119 language'],
      ['JSON Schema', 'machine-checkable'],
      ['17 vectors', 'written from the spec, not the code'],
      ['A second engine', 'agrees on all 17']
    ].map(([label, note], i) => (
      <div key={label} className="flex items-center gap-2">
        <span className="flex h-3.5 w-3.5 flex-none items-center justify-center rounded-[4px] bg-brand-100 font-mono text-[8px] font-bold text-brand-700 dark:bg-brand-500/20 dark:text-brand-300">
          {i + 1}
        </span>
        <span className="text-[10px] font-semibold leading-3 text-slate-700 dark:text-slate-200">{label}</span>
        <span className="truncate text-[9px] leading-3 text-slate-400 dark:text-slate-500">{note}</span>
      </div>
    ))}
  </div>
);

/** The frameworks a policy is mapped to, named. */
export const FrameworkVisual: React.FC = () => (
  <div className={`${FRAME} flex flex-col justify-center gap-2.5 px-3 py-2`} style={{ height: H }}>
    {[
      ['OWASP', 'Top 10 for LLMs', 'carried on policies'],
      ['NIST', 'AI RMF', 'carried on policies'],
      ['MITRE', 'CWE', 'crosswalked, 6 out of scope']
    ].map(([org, name, note]) => (
      <div key={org} className="flex items-center gap-2">
        <span className="w-12 flex-none rounded-[4px] bg-slate-200/80 px-1 py-0.5 text-center font-mono text-[8px] font-bold text-slate-600 dark:bg-white/10 dark:text-slate-300">
          {org}
        </span>
        <span className="text-[10px] font-semibold leading-3 text-slate-700 dark:text-slate-200">{name}</span>
        <span className="truncate text-[9px] leading-3 text-slate-400 dark:text-slate-500">{note}</span>
      </div>
    ))}
  </div>
);

/** The MCP tools, as a coding agent would see them. */
export const McpVisual: React.FC<{ dark: boolean }> = ({ dark }) => (
  <div
    className="flex flex-col justify-center overflow-hidden rounded-xl border border-slate-900/10 px-3 py-2 dark:border-white/10"
    style={{ height: H, background: dark ? '#05070f' : '#0f172a' }}
  >
    <p className="font-mono text-[8.5px] leading-[14px] text-slate-500">tools/list</p>
    {[
      ['assess_agent', 'no credential'],
      ['validate_profile', 'no credential'],
      ['evaluate_action', 'no credential'],
      ['…6 more', 'read your fleet']
    ].map(([tool, note]) => (
      <p key={tool} className="font-mono text-[8.5px] leading-[14px]">
        <span className="text-emerald-400">→ </span>
        <span className="text-slate-200">{tool}</span>
        <span className="text-slate-500">  {note}</span>
      </p>
    ))}
  </div>
);
